import Clickable from '/components/Clickable';
import Icon from './IconUpdated';
import Loading from './Loading';
import PropTypes from 'prop-types';
import React from 'react';
import Stars from '/components/Stars';
import Typography from '/components/Typography';
import { parseGid } from '/utils/graph-ql';
import { pluralize } from '/utils/pluralize';
import { getReviewSummary, getReviews, getStars } from '/services/okendo';
import { useEffect, useState } from 'react';

const getWidthStyles = (numReviews, totalCount) => {
	if (numReviews === 0) {
		return { width: '0%' };
	}
	return { width: (numReviews / totalCount) * 100 + '%' };
};

const ReviewBars = ({ breakdown, count }) => {
	if (!breakdown) {
		return null;
	}

	return (
		<>
			{[5, 4, 3, 2, 1].map((rating) => {
				const styles = getWidthStyles(breakdown[rating], count);

				return (
					<div className="grid grid-cols-2 pr-6" key={`rating-${rating}`}>
						<div>
							<Typography component="p" variant="body">
								{rating} {pluralize('Star', rating)}
							</Typography>
						</div>
						<div className="w-full bg-gray-200 h-3 rounded-lg mt-2">
							<div className="bg-red-600 h-3 rounded-lg" style={styles}></div>
						</div>
					</div>
				);
			})}
		</>
	);
};

const ReviewRow = ({ review }) => {
	return (
		<div
			className="grid grid-cols-1 md:grid-cols-3 gap-4 py-4 border-b-2 border-v2-brown"
			id="reviews-section">
			<div className="col-span-1">
				<div>
					{review.customer && (
						<Typography className="overflow-hidden" component="div" variant="body">
							{review.customer.nameFirst} {review.customer.nameLast}
						</Typography>
					)}
					<Typography className="text-v2-primary" component="div" variant="body">
						{review.isVerified ? 'Verified' : ''}
					</Typography>
				</div>
				<div>
					<Stars rating={review.rating} />
				</div>
				{review?.createdAt && (
					<div>
						<Typography component="p" variant="body">
							{review?.createdAt?.substring(0, 10)}
						</Typography>
					</div>
				)}
			</div>
			<div className="col-span-1 md:col-span-2">
				<div>
					<Typography className="overflow-hidden font-bold" variant="body-lg">
						<span dangerouslySetInnerHTML={{ __html: review.title }}></span>
					</Typography>
				</div>
				<Typography className="mb-4 overflow-hidden" component="div" variant="body">
					<span dangerouslySetInnerHTML={{ __html: review.body }}></span>
				</Typography>
				{review.attributeRecords?.heightWeight && (
					<>
						<Typography component="div" variant="subtitle-sm">
							Height &amp; Weight:
						</Typography>
						<Typography className="mb-4" component="p" variant="body">
							{review.attributeRecords?.heightWeight}
						</Typography>
					</>
				)}
				{review.attributeRecords?.sizePurchased && (
					<>
						<Typography component="div" variant="subtitle-sm">
							Size Purchased:
						</Typography>
						<Typography className="mb-4" component="p" variant="body">
							{review.attributeRecords?.sizePurchased}
						</Typography>
					</>
				)}
				{review.attributeRecords?.usuallyWears && (
					<>
						<Typography component="div" variant="subtitle-sm">
							Usually Wears:
						</Typography>
						<Typography className="mb-4" component="p" variant="body">
							{review.attributeRecords?.usuallyWears}
						</Typography>
					</>
				)}
			</div>
		</div>
	);
};

const ReviewSection = ({ productId }) => {
	productId = parseGid(productId) || productId;

	const [isLoading, setIsLoading] = useState(true);
	const [allReviews, setAllReviews] = useState([]);
	const [stars, setStars] = useState();
	const [hasMoreReviews, setHasMoreReviews] = useState(true);
	const [nextUrl, setNextUrl] = useState(null);
	const [summary, setSummary] = useState('');

	useEffect(() => {
		async function fetchReviews() {
			setIsLoading(true);

			const starsResponse = await getStars(productId);
			const reviewsResponse = await getReviews(productId);
			if (starsResponse) {
				setStars(starsResponse);
			}

			if (reviewsResponse) {
				setAllReviews(reviewsResponse.reviews);
				setNextUrl(reviewsResponse.meta?.page?.after);
				setHasMoreReviews(reviewsResponse.meta?.page?.after !== null);
			}

			const summary = await getReviewSummary(productId)
			if (summary) {
				setSummary(summary.body)
			}

			setIsLoading(false);
		}

		fetchReviews();
	}, [productId]);

	const handleShowMore = async () => {
		if (nextUrl) {
			const reviewsResponse = await getReviews(productId, nextUrl);
			if (reviewsResponse?.reviews) {
				setAllReviews((prev) => [...prev, ...reviewsResponse.reviews]);
				setNextUrl(reviewsResponse.meta?.page?.after);
				setHasMoreReviews(reviewsResponse.meta?.page?.after !== null);
			}
		}
	};

	if (isLoading) {
		return (
			<div className="h-[440px] flex items-center align-middle justify-center">
				<Loading className="ml-2" height="100px" width="100px" />
			</div>
		);
	}

	if (!isLoading && !stars?.ratingAverage && (!allReviews || allReviews.length === 0)) {
		return null;
	}

	return (
		<div className="container max-w-6xl px-4 md:px-0">
			<div className="py-4 text-center sm:text-left">
				<Typography className="text-center" component="h3" variant="subhead-md">
					Customer Reviews
				</Typography>
			</div>
			{summary && (
				<div className="mb-4 lg:max-w-2xl mx-auto">
					<Typography className="text-center mb-2" variant="subhead-sm" component="h4">
						Review Summary{' '}
						<Icon
							className="inline align-baseline"
							name="sparkle"
							height="16px"
							width="16px"
						/>
					</Typography>
					<p>{summary}</p>
				</div>
			)}
			{stars && (
				<div className="grid grid-cols-3 gap-4 border-2 border-v2-brown py-4">
					<div className="flex items-center justify-center text-center col-span-3 sm:col-span-1 border-0 md:border-r-2 border-v2-brown">
						<div>
							<Stars rating={stars.ratingAverage} />
							<Typography className="text-center" component="div" variant="body">
								{stars.ratingCount} {pluralize('Review', stars.ratingCount)}
							</Typography>
						</div>
					</div>
					<div className="text-center mb-4 col-span-3 sm:col-span-1">
						<ReviewBars breakdown={stars.ratingDistribution} count={stars.ratingCount} />
					</div>

					<div className="flex col-span-3 sm:col-span-1 items-center justify-center border-0 md:border-l-2 border-v2-brown">
						{stars?.fit?.value && (
							<div className="text-center">
								<Typography className="text-base mb-2" variant="body">
									Fit
								</Typography>
								<div className="relative w-48 h-2 bg-gray-200 rounded-full mb-2">
									<div
										className="absolute h-2 bg-red-600 rounded-full"
										style={{
											width: '20px',
											left: `${(stars.fit.value + 1) * 50}%`,
											transform: 'translateX(-50%)',
										}}
									/>
								</div>
								<div className="flex justify-between text-sm">
									<span>Small</span>
									<span>True</span>
									<span>Large</span>
								</div>
							</div>
						)}
					</div>
				</div>
			)}

			{allReviews.length > 0 && (
				<>
					<div className="mb-4" id="reviews-heading">
						<Typography
							variant="subhead-sm"
							component="h4"
							className="pt-4 pb-2 border-b-2 border-v2-brown">
							Reviews
						</Typography>
						{allReviews.map((review) => {
							return <ReviewRow key={review.id} review={review} />;
						})}
					</div>
					{hasMoreReviews && (
						<div className="flex justify-center mb-8">
							<Clickable
								className="border-2 border-v2-primary outline-none rounded-full px-8 py-2 hover:bg-gray-200 active:bg-gray-200 focus:shadow-inner shadow-v2-primary-500/50 text-v2-primary"
								onClick={handleShowMore}>
								Show More Reviews
							</Clickable>
						</div>
					)}
				</>
			)}
		</div>
	);
};

ReviewBars.propTypes = {
	breakdown: PropTypes.object,
	count: PropTypes.number,
};

ReviewRow.propTypes = {
	review: PropTypes.object,
};

ReviewSection.propTypes = {
	pageNum: PropTypes.number,
	productId: PropTypes.string,
};

export default ReviewSection;
